import { RefObject, useEffect, useRef, useState } from 'react'
import { Box, Flex, useColorModeValue, useToken } from '@chakra-ui/react'
import { keyframes } from '@emotion/react'

const girar = keyframes`from { transform: rotate(0deg) } to { transform: rotate(360deg) }`

/** Distância (px, já amortecida) que dispara a atualização ao soltar. */
const GATILHO = 64
/** Quanto o indicador pode descer no máximo. */
const LIMITE = 96

/**
 * "Puxar para atualizar" das listas no celular: com a lista no topo, arrastar
 * para baixo e soltar dispara `onRefresh` e mostra o carregando até a busca
 * terminar. No desktop não há toque, então nada muda.
 */
export function usePullToRefresh(
  ref: RefObject<HTMLElement>,
  onRefresh: () => Promise<unknown>,
) {
  const [pull, setPull] = useState(0)
  const [refreshing, setRefreshing] = useState(false)
  const [arrastando, setArrastando] = useState(false)
  const onRefreshRef = useRef(onRefresh)
  onRefreshRef.current = onRefresh

  useEffect(() => {
    const el = ref.current
    if (!el) return

    let inicioY = 0
    let puxando = false
    let atual = 0
    let ocupado = false

    const start = (e: TouchEvent) => {
      if (ocupado || el.scrollTop > 0) {
        puxando = false
        return
      }
      inicioY = e.touches[0].clientY
      puxando = true
      atual = 0
      setArrastando(true)
    }

    const move = (e: TouchEvent) => {
      if (!puxando) return
      const dy = e.touches[0].clientY - inicioY
      if (dy <= 0 || el.scrollTop > 0) {
        if (atual) {
          atual = 0
          setPull(0)
        }
        if (el.scrollTop > 0) puxando = false
        return
      }
      const antes = atual
      atual = Math.min(LIMITE, dy * 0.5)
      // Toque leve ao passar do ponto de soltar (Android; iOS ignora).
      if (antes < GATILHO && atual >= GATILHO) navigator.vibrate?.(10)
      setPull(atual)
      // Segura o arrasto para a página não rolar junto (iOS faz bounce).
      if (e.cancelable) e.preventDefault()
    }

    const end = async () => {
      if (!puxando) return
      puxando = false
      setArrastando(false)
      const disparar = atual >= GATILHO
      atual = 0
      if (!disparar) {
        setPull(0)
        return
      }
      ocupado = true
      setRefreshing(true)
      setPull(GATILHO)
      try {
        await onRefreshRef.current()
      } catch {
        // a própria busca já registra o erro
      } finally {
        ocupado = false
        setRefreshing(false)
        setPull(0)
      }
    }

    el.addEventListener('touchstart', start, { passive: true })
    el.addEventListener('touchmove', move, { passive: false })
    el.addEventListener('touchend', end)
    el.addEventListener('touchcancel', end)
    return () => {
      el.removeEventListener('touchstart', start)
      el.removeEventListener('touchmove', move)
      el.removeEventListener('touchend', end)
      el.removeEventListener('touchcancel', end)
    }
  }, [ref])

  return { pull, refreshing, arrastando }
}

const RAIO = 9
const CIRCUNFERENCIA = 2 * Math.PI * RAIO

/**
 * Indicador no estilo dos apps: um círculo desce acompanhando o dedo e o arco
 * vai se completando e girando conforme puxa. Solto no ponto, gira até a
 * busca terminar e recolhe suave. Fica sempre montado (altura 0) para a
 * volta também animar.
 */
export function PullToRefreshIndicator({
  pull,
  refreshing,
  arrastando = false,
}: {
  pull: number
  refreshing: boolean
  arrastando?: boolean
}) {
  const fundo = useColorModeValue('white', 'gray.700')
  const [trilho, arco] = useToken('colors', [
    useColorModeValue('gray.200', 'gray.600'),
    'yellow.400',
  ])
  const altura = refreshing ? GATILHO : pull
  const progresso = Math.min(1, pull / GATILHO)
  const suave = arrastando ? undefined : 'cubic-bezier(.2,.8,.2,1)'
  return (
    <Flex
      h={`${altura}px`}
      flexShrink={0}
      align="center"
      justify="center"
      overflow="hidden"
      aria-hidden={altura <= 0}
      transition={suave && `height 0.25s ${suave}`}
    >
      <Flex
        w="36px"
        h="36px"
        borderRadius="full"
        bg={fundo}
        boxShadow="md"
        align="center"
        justify="center"
        opacity={refreshing ? 1 : Math.min(1, progresso * 1.4)}
        transform={`scale(${refreshing ? 1 : 0.6 + 0.4 * progresso})`}
        transition={suave && `transform 0.25s ${suave}, opacity 0.25s`}
      >
        <Box
          as="svg"
          viewBox="0 0 24 24"
          w="22px"
          h="22px"
          animation={refreshing ? `${girar} 0.8s linear infinite` : undefined}
          transform={refreshing ? undefined : `rotate(${progresso * 270}deg)`}
        >
          <circle cx="12" cy="12" r={RAIO} fill="none" stroke={trilho} strokeWidth="2.5" />
          <circle
            cx="12"
            cy="12"
            r={RAIO}
            fill="none"
            stroke={arco}
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeDasharray={CIRCUNFERENCIA}
            strokeDashoffset={
              refreshing ? CIRCUNFERENCIA * 0.7 : CIRCUNFERENCIA * (1 - 0.8 * progresso)
            }
            transform="rotate(-90 12 12)"
            opacity={progresso >= 1 || refreshing ? 1 : 0.7}
          />
        </Box>
      </Flex>
    </Flex>
  )
}
